import {
  addressDomain,
  MICROSOFT365_WRITE_LIMITS,
  microsoft365Address,
  microsoft365WriteCard,
  microsoft365WriteOutcome,
  microsoft365WritePayload,
  microsoft365WriteView,
  normalizeLineEnds,
  zonedDateKey,
  zonedParts,
  type Microsoft365MailEdits,
  type Microsoft365MailPayload,
  type Microsoft365MailView,
  type Microsoft365MeetingEdits,
  type Microsoft365MeetingPayload,
  type Microsoft365MeetingView,
  type Microsoft365Recipient,
  type Microsoft365WriteKind,
  type Microsoft365WriteOutcome,
  type Microsoft365WritePayload,
  type Microsoft365WriteTool,
  type Microsoft365WriteView,
} from '@dbrain/shared';
import { cardZone, clockOf, instantOf, senderName, type Translate } from './answer-cards.js';

/**
 * Microsoft 365 write cards on the screen (part 3, D-220…; MsSohbetOnay, MsToplantiOnay): the mail and meeting cards
 * read the canonical payload text the worker stored with the approval (what is sent, exactly) and its display-only
 * view (Kimden, the answered mail, free/busy). This parses both, lays out the recipients with their "Şirket dışı"
 * notes, says who a card goes to with the Turkish dative ("Ayşe'ye", "Ayşe ve Can'a"), builds the meeting's date tile,
 * time line, attendee availability and the free/busy strip with a text for every cell, keeps the person's edits
 * (Düzenle, "Başka saat seç") as the send body's `edits`, and words every state a card ends in, the unknown one
 * included. Nothing here sends or hashes: the browser echoes the server's hash. DOM-free, shared with the phone.
 */
const K = 'microsoft365.chat.write';

// ---- the card and its end, parsed ------------------------------------------------------------------

export interface WriteCardView {
  tool: Microsoft365WriteTool;
  kind: Microsoft365WriteKind;
  /** the hash the browser echoes (approvals.request.microsoft365.hash) */
  hash: string;
  /** the canonical payload as the worker built it, before any edit */
  payload: Microsoft365WritePayload;
  /** display-only facts; null when unreadable or of another kind (the card then shows what the payload says) */
  view: Microsoft365WriteView | null;
}

function json(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

/** A payload text of the card's kind, or null (a card whose payload cannot be read is never offered for sending). */
export function parsePayload(text: string | undefined | null, kind: Microsoft365WriteKind): Microsoft365WritePayload | null {
  if (typeof text !== 'string') return null;
  const parsed = microsoft365WritePayload.safeParse(json(text));
  return parsed.success && parsed.data.kind === kind ? parsed.data : null;
}

/** `approvals.request.microsoft365` as the card draws it; null when anything needed to show what is sent is missing. */
export function parseWriteCard(card: unknown): WriteCardView | null {
  const parsed = microsoft365WriteCard.safeParse(card);
  if (!parsed.success) return null;
  const { tool, kind, hash } = parsed.data;
  const payload = parsePayload(parsed.data.payload, kind);
  if (!payload) return null;
  const view = microsoft365WriteView.safeParse(json(parsed.data.view));
  return { tool, kind, hash, payload, view: view.success && view.data.kind === kind ? view.data : null };
}

/** `approval.resolved.microsoft365`, or null. */
export function parseOutcome(outcome: unknown): Microsoft365WriteOutcome | null {
  const parsed = microsoft365WriteOutcome.safeParse(outcome);
  return parsed.success ? parsed.data : null;
}

export type WriteState = 'pending' | 'sent' | 'created' | 'failed' | 'unknown' | 'expired' | 'cancelled' | 'stopped';

/**
 * How a card stands: pending while its approval waits; else its outcome. An approval that ended without one
 * (an older server) never reads as sent: approved is unknown, rejected is "vazgeçtiniz", cancelled is "durduruldu".
 */
export function writeState(status: 'pending' | 'approved' | 'rejected' | 'expired' | 'cancelled', outcome: Pick<Microsoft365WriteOutcome, 'outcome'> | null): WriteState {
  if (status === 'pending') return 'pending';
  if (outcome) return outcome.outcome;
  switch (status) {
    case 'rejected':
      return 'cancelled';
    case 'cancelled':
      return 'stopped';
    case 'expired':
      return 'expired';
    default:
      return 'unknown';
  }
}

// ---- people --------------------------------------------------------------------------------------

const lower = (value: string) => value.trim().toLowerCase();

/** A person's display name, or their address when the name is missing (a typed address). */
export const personLabel = (person: { name: string; address: string }): string => senderName(person);

/** "AD" for Ayşe Demir; the address's first letter for a typed address. */
export function initialsOf(person: { name: string; address: string }): string {
  const name = person.name.trim();
  if (!name || name.includes('@')) return (person.address.trim()[0] ?? '?').toLocaleUpperCase('tr');
  const words = name.split(' · ')[0]?.split(/\s+/).filter(Boolean) ?? [];
  return words
    .slice(0, 2)
    .map((word) => word[0] ?? '')
    .join('')
    .toLocaleUpperCase('tr');
}

export interface RecipientChip {
  name: string;
  address: string;
  /** what the chip says: the name, or the address alone */
  label: string;
  initials: string;
  /** outside the account's domain: "Şirket dışı" */
  external: boolean;
  /** the answered mail's Reply-To put this address here */
  replyTo: boolean;
}

/**
 * The recipients as chips, in order. The worker's flags say who is outside the account's domain; an address the
 * person typed in Düzenle has none, and is compared with the account's own domain (Kimden).
 */
export function recipientChips(
  people: readonly Microsoft365Recipient[],
  flags: readonly { address: string; external: boolean; replyTo?: boolean }[] = [],
  accountAddress?: string | null,
): RecipientChip[] {
  const own = accountAddress ? addressDomain(accountAddress) : null;
  return people.map((person) => {
    const flag = flags.find((f) => lower(f.address) === lower(person.address));
    return {
      name: person.name,
      address: person.address,
      label: personLabel(person),
      initials: initialsOf(person),
      external: flag ? flag.external : own !== null && addressDomain(person.address) !== own,
      replyTo: flag?.replyTo === true,
    };
  });
}

// ---- Turkish: "Ayşe'ye", "Can'a", "Ayşe ve Can'a" ------------------------------------------------------

const BACK = new Set(['a', 'ı', 'o', 'u', 'â', 'û']);
const VOWELS = new Set(['a', 'ı', 'o', 'u', 'â', 'û', 'e', 'i', 'ö', 'ü', 'î']);

/**
 * The Turkish dative ending of a name, with its apostrophe: by the last vowel (back: -a, front: -e) and a buffer y
 * after a vowel: Ayşe'ye, Can'a, Murat'a, Selin'e, Kaya'ya, Öz'e. Letters read one by one end in a vowel: BT'ye.
 */
export function trDativeSuffix(name: string): string {
  const letters = name.toLocaleLowerCase('tr').replace(/[^a-zçğıöşüâîû]/g, '');
  const vowels = [...letters].filter((ch) => VOWELS.has(ch));
  if (vowels.length === 0) return "'ye";
  const vowel = BACK.has(vowels.at(-1) ?? 'e') ? 'a' : 'e';
  return VOWELS.has(letters.at(-1) ?? '') ? `'y${vowel}` : `'${vowel}`;
}

/** How the card names a person in a sentence: the first name, the full name when two share it, the address when there is no name. */
function shortNames(people: readonly { name: string; address: string }[]): { text: string; address: boolean }[] {
  const full = people.map((p) => (p.name.trim() && !p.name.includes('@') ? (p.name.split(' · ')[0] ?? p.name).trim() : ''));
  const first = full.map((name) => name.split(/\s+/)[0] ?? name);
  return people.map((p, i) => {
    if (!full[i]) return { text: p.address.trim(), address: true };
    const shared = first.filter((name) => name && lower(name) === lower(first[i] ?? '')).length > 1;
    return { text: shared ? (full[i] as string) : (first[i] as string), address: false };
  });
}

/**
 * Who a card goes to, as its footer says it: "Ayşe'ye", "Ayşe ve Can'a", "Ayşe, Can ve Elif'e", "Ayşe, Can ve 3
 * kişiye" (a typed address: "… adresine"); in English "Ayşe", "Ayşe and Can", "Ayşe, Can and 3 others".
 */
export function recipientsPhrase(people: readonly { name: string; address: string }[], locale: string): string {
  const names = shortNames(people);
  if (names.length === 0) return '';
  if (!locale.startsWith('tr')) {
    const shown = names.map((n) => n.text);
    if (shown.length === 1) return shown[0] as string;
    if (shown.length === 2) return `${shown[0]} and ${shown[1]}`;
    if (shown.length === 3) return `${shown[0]}, ${shown[1]} and ${shown[2]}`;
    return `${shown[0]}, ${shown[1]} and ${shown.length - 2} others`;
  }
  const dative = (n: { text: string; address: boolean }) => (n.address ? `${n.text} adresine` : `${n.text}${trDativeSuffix(n.text)}`);
  const [a, b, c] = names as [{ text: string; address: boolean }, { text: string; address: boolean }?, { text: string; address: boolean }?];
  if (!b) return dative(a);
  if (names.length === 2) return `${a.text} ve ${dative(b)}`;
  if (names.length === 3 && c) return `${a.text}, ${b.text} ve ${dative(c)}`;
  return `${a.text}, ${b.text} ve ${names.length - 2} kişiye`;
}

/** The mail card's footer: "Gönderince sizin hesabınızdan Ayşe'ye gider." (To, then Cc); none while Düzenle left nobody. */
export function mailFooter(payload: Pick<Microsoft365MailPayload, 'to' | 'cc'>, t: Translate, locale: string): string {
  const people = [...payload.to, ...payload.cc];
  return people.length > 0 ? t(`${K}.footer.mail`, { people: recipientsPhrase(people, locale) }) : '';
}

/** The meeting card's footer: "Oluşturunca davet sizin adınıza Ayşe ve Can'a gider."; nobody else: it only goes into the calendar. */
export function meetingFooter(payload: Pick<Microsoft365MeetingPayload, 'attendees'>, t: Translate, locale: string): string {
  if (payload.attendees.length === 0) return t(`${K}.footer.alone`);
  return t(`${K}.footer.meeting`, { people: recipientsPhrase(payload.attendees, locale) });
}

// ---- time ----------------------------------------------------------------------------------------

const DAY_MS = 86_400_000;
const pad = (n: number) => String(n).padStart(2, '0');

/** Midday UTC of a date: its weekday and month names, whatever the zone. */
function middayOf(date: string): Date {
  const [y = 1970, m = 1, d = 1] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12));
}

/** Days since 1970 of a YYYY-MM-DD date. */
const dayNumber = (date: string) => Math.floor(middayOf(date).getTime() / DAY_MS);

/** How far the zone's clock is ahead of UTC at an instant, in milliseconds. */
function offsetAt(instant: number, zone: string): number {
  const p = zonedParts(new Date(instant), zone);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(instant / 1000) * 1000;
}

/** The instant a day (YYYY-MM-DD) and a clock time (HH:MM) name in a zone; null for an impossible day or time. */
export function instantAt(date: string, time: string, timeZone: string): Date | null {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const clock = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(time);
  if (!day || !clock) return null;
  const [y, m, d] = [Number(day[1]), Number(day[2]), Number(day[3])];
  const probe = new Date(Date.UTC(y, m - 1, d));
  if (probe.getUTCMonth() !== m - 1 || probe.getUTCDate() !== d) return null;
  const zone = cardZone(timeZone);
  const wall = Date.UTC(y, m - 1, d, Number(clock[1]), Number(clock[2]));
  const first = wall - offsetAt(wall, zone);
  // the offset at the answer differs when a clock change lies between: once more with that one
  return new Date(wall - offsetAt(first, zone));
}

/** The day (YYYY-MM-DD) and the clock time (HH:MM) of an instant in a zone. */
export function wallOf(at: Date, timeZone: string): { date: string; time: string } {
  const zone = cardZone(timeZone);
  const p = zonedParts(at, zone);
  return { date: zonedDateKey(at, zone), time: `${pad(p.hour)}:${pad(p.minute)}` };
}

/** The date tile: "EKİM" over "31" over "Cuma"; the month's whole name in capitals of the language (tr: İ). */
export function dateTile(at: Date, timeZone: string, locale: string): { month: string; day: string; weekday: string } {
  const date = zonedDateKey(at, cardZone(timeZone));
  const midday = middayOf(date);
  const upper = locale.startsWith('tr') ? 'tr' : locale;
  return {
    month: midday.toLocaleDateString(locale, { month: 'long', timeZone: 'UTC' }).toLocaleUpperCase(upper),
    day: String(midday.getUTCDate()),
    weekday: midday.toLocaleDateString(locale, { weekday: 'long', timeZone: 'UTC' }),
  };
}

/** "30 dakika", "1 saat", "1 saat 30 dakika". */
export function minutesText(minutes: number, t: Translate): string {
  const total = Math.max(0, Math.round(minutes));
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  if (hours > 0 && rest > 0) return t(`${K}.meeting.duration.hoursMinutes`, { hours, minutes: rest });
  if (hours > 0) return t(`${K}.meeting.duration.hours`, { count: hours });
  return t(`${K}.meeting.duration.minutes`, { count: rest });
}

export interface MeetingTime {
  start: Date;
  end: Date;
  /** the zone's day and clock times */
  date: string;
  from: string;
  to: string;
  minutes: number;
}

export function meetingTime(start: Date, end: Date, timeZone: string): MeetingTime {
  const zone = cardZone(timeZone);
  return {
    start,
    end,
    date: zonedDateKey(start, zone),
    from: clockOf(start.toISOString(), zone),
    to: clockOf(end.toISOString(), zone),
    minutes: Math.round((end.getTime() - start.getTime()) / 60_000),
  };
}

/** The time line: "15:30 – 16:00" and "30 dakika". */
export function timeLine(time: MeetingTime, t: Translate): { range: string; length: string } {
  return { range: t(`${K}.meeting.time`, { from: time.from, to: time.to }), length: minutesText(time.minutes, t) };
}

// ---- free/busy: the chips and the strip -------------------------------------------------------------

export type Schedule = NonNullable<Microsoft365MeetingView['schedule']>;
export type Availability = 'free' | 'busy' | 'unseen' | 'unchecked';

const overlaps = (aStart: number, aEnd: number, bStart: number, bEnd: number) => aStart < bEnd && bStart < aEnd;

function busyOf(person: Schedule['people'][number]): { start: number; end: number }[] | null {
  if (!person.busy) return null;
  return person.busy.flatMap((b) => {
    const start = instantOf(b.start)?.getTime();
    const end = instantOf(b.end)?.getTime();
    return start !== undefined && end !== undefined && end > start ? [{ start, end }] : [];
  });
}

/** The strip's window covers the chosen time: the chips and cells say something about it. */
export function scheduleCovers(schedule: Schedule | null, start: Date, end: Date): boolean {
  if (!schedule) return false;
  const from = instantOf(schedule.start)?.getTime();
  const to = instantOf(schedule.end)?.getTime();
  return from !== undefined && to !== undefined && start.getTime() >= from && end.getTime() <= to;
}

/**
 * One person at the chosen time, from the free/busy the card came with (so "Başka saat seç" recolours the chips
 * without a call): "bu saatte boş", "bu saatte dolu", "takvimi görülemedi"; "takvime bakılmadı" outside the window.
 */
export function availabilityAt(schedule: Schedule | null, address: string, start: Date, end: Date): Availability {
  if (!schedule || !scheduleCovers(schedule, start, end)) return 'unchecked';
  const person = schedule.people.find((p) => lower(p.address) === lower(address));
  if (!person) return 'unchecked';
  const busy = busyOf(person);
  if (!busy) return 'unseen';
  return busy.some((b) => overlaps(b.start, b.end, start.getTime(), end.getTime())) ? 'busy' : 'free';
}

export type StripCellState = 'busy' | 'selected' | 'alternative' | 'free';

export interface StripCell {
  start: Date;
  end: Date;
  from: string;
  to: string;
  state: StripCellState;
  /** people busy in the cell (calendars that could not be seen are not counted) */
  busy: number;
  /** a whole hour starts here: the label under the strip */
  hour: string | null;
}

/** at most this many cells on a strip (a whole working day in half hours fits) */
const MAX_CELLS = 48;

/**
 * The strip (MsToplantiOnay): the window in cells of `interval` minutes. A cell is "seçilen" where the chosen meeting
 * is, "dolu" where anyone is busy, "başka uygun" inside an offered alternative, else plain: everyone free but not
 * offered. Busy wins over an alternative, so a stale alternative never reads free.
 */
export function stripCells(schedule: Schedule, chosen: { start: Date; end: Date }, alternatives: readonly Date[], timeZone: string): StripCell[] {
  const zone = cardZone(timeZone);
  const from = instantOf(schedule.start)?.getTime();
  const to = instantOf(schedule.end)?.getTime();
  if (from === undefined || to === undefined || to <= from) return [];
  const step = schedule.interval * 60_000;
  const people = schedule.people.map(busyOf);
  const length = chosen.end.getTime() - chosen.start.getTime();
  const offered = alternatives.map((a) => a.getTime()).filter((a) => a !== chosen.start.getTime());
  const cells: StripCell[] = [];
  for (let at = from; at < to && cells.length < MAX_CELLS; at += step) {
    const end = Math.min(at + step, to);
    const busy = people.filter((busyList) => busyList?.some((b) => overlaps(b.start, b.end, at, end))).length;
    const state: StripCellState = overlaps(at, end, chosen.start.getTime(), chosen.end.getTime())
      ? 'selected'
      : busy > 0
        ? 'busy'
        : offered.some((a) => overlaps(at, end, a, a + length))
          ? 'alternative'
          : 'free';
    const p = zonedParts(new Date(at), zone);
    cells.push({
      start: new Date(at),
      end: new Date(end),
      from: clockOf(new Date(at).toISOString(), zone),
      to: clockOf(new Date(end).toISOString(), zone),
      state,
      busy,
      hour: p.minute === 0 ? `${pad(p.hour)}:00` : null,
    });
  }
  return cells;
}

/** A cell's text alternative: "15:30–16:00 · seçilen · herkes boş", "13:00–13:30 · dolu". */
export function cellText(cell: StripCell, t: Translate): string {
  const state =
    cell.state === 'selected' && cell.busy > 0 ? t(`${K}.meeting.strip.state.selectedBusy`, { count: cell.busy }) : t(`${K}.meeting.strip.state.${cell.state}`);
  return t(`${K}.meeting.strip.cell`, { from: cell.from, to: cell.to, state });
}

/** How many people the strip is about: "Üçünüzün takvimi" (the organizer counts). */
function whose(count: number, t: Translate): string {
  if (count <= 1) return t(`${K}.meeting.strip.whose.1`);
  if (count <= 10) return t(`${K}.meeting.strip.whose.${count}`);
  return t(`${K}.meeting.strip.whose.many`, { count });
}

/**
 * The strip's title: "Üçünüzün takvimi, yarın öğleden sonra": whose calendars, the day against today in the zone
 * ("bugün", "yarın", else "31 Ekim") and the half of the day the window lies in.
 */
export function stripTitle(schedule: Schedule, people: number, timeZone: string, now: Date, locale: string, t: Translate): string {
  const zone = cardZone(timeZone);
  const start = instantOf(schedule.start);
  const end = instantOf(schedule.end);
  if (!start || !end) return whose(people, t);
  const date = zonedDateKey(start, zone);
  const diff = dayNumber(date) - dayNumber(zonedDateKey(now, zone));
  const day =
    diff === 0
      ? t(`${K}.meeting.strip.day.today`)
      : diff === 1
        ? t(`${K}.meeting.strip.day.tomorrow`)
        : middayOf(date).toLocaleDateString(locale, { day: 'numeric', month: 'long', timeZone: 'UTC' });
  const s = zonedParts(start, zone);
  const e = zonedParts(new Date(end.getTime() - 1), zone);
  const sameDay = zonedDateKey(new Date(end.getTime() - 1), zone) === date;
  const part = sameDay && s.hour >= 12 ? 'afternoon' : sameDay && e.hour < 13 ? 'morning' : null;
  const when = part ? t(`${K}.meeting.strip.when`, { day, part: t(`${K}.meeting.strip.part.${part}`) }) : day;
  return t(`${K}.meeting.strip.title`, { whose: whose(people, t), when });
}

/**
 * "Başka saat seç": the worker's offered starts and the card's own time (to go back to it), without the time chosen
 * now, at most three, earliest first.
 */
export function otherTimes(schedule: Schedule | null, original: Date, chosen: Date, timeZone: string): { at: Date; time: string }[] {
  const zone = cardZone(timeZone);
  const seen = new Set<number>([chosen.getTime()]);
  const out: { at: Date; time: string }[] = [];
  for (const at of [original, ...(schedule?.alternatives ?? []).map((iso) => instantOf(iso)).filter((d): d is Date => d !== null)]) {
    if (seen.has(at.getTime())) continue;
    seen.add(at.getTime());
    out.push({ at, time: clockOf(at.toISOString(), zone) });
  }
  return out.sort((a, b) => a.at.getTime() - b.at.getTime()).slice(0, 3);
}

// ---- Düzenle: the fields and the send body's edits ------------------------------------------------------

export interface MailFields {
  to: Microsoft365Recipient[];
  cc: Microsoft365Recipient[];
  subject: string;
  body: string;
}

export const mailFields = (payload: Microsoft365MailPayload): MailFields => ({ to: [...payload.to], cc: [...payload.cc], subject: payload.subject, body: payload.body });

const samePeople = (a: readonly Microsoft365Recipient[], b: readonly Microsoft365Recipient[]) =>
  a.length === b.length && a.every((p, i) => lower(p.address) === lower(b[i]?.address ?? '') && p.name === b[i]?.name);

/** The send body's mail edits: only what differs from the card; undefined when nothing does. */
export function mailEdits(payload: Microsoft365MailPayload, fields: MailFields): Microsoft365MailEdits | undefined {
  const edits: Microsoft365MailEdits = { kind: 'mail' };
  if (!samePeople(payload.to, fields.to)) edits.to = fields.to;
  if (!samePeople(payload.cc, fields.cc)) edits.cc = fields.cc;
  if (fields.subject !== payload.subject) edits.subject = fields.subject;
  if (normalizeLineEnds(fields.body) !== payload.body) edits.body = normalizeLineEnds(fields.body);
  return Object.keys(edits).length > 1 ? edits : undefined;
}

/** The mail as the card shows it while edited (what "Gönder" would send). */
export const editedMail = (payload: Microsoft365MailPayload, fields: MailFields): Microsoft365MailPayload => ({ ...payload, ...fields, body: normalizeLineEnds(fields.body) });

export interface MeetingFields {
  subject: string;
  /** YYYY-MM-DD and HH:MM in the payload's zone */
  date: string;
  start: string;
  durationMinutes: number;
  attendees: Microsoft365Recipient[];
  note: string;
}

export function meetingFields(payload: Microsoft365MeetingPayload): MeetingFields {
  const start = instantOf(payload.start) ?? new Date(0);
  const end = instantOf(payload.end) ?? start;
  const wall = wallOf(start, payload.timeZone);
  return {
    subject: payload.subject,
    date: wall.date,
    start: wall.time,
    durationMinutes: Math.round((end.getTime() - start.getTime()) / 60_000),
    attendees: [...payload.attendees],
    note: payload.note,
  };
}

/** The send body's meeting edits: only what differs from the card; the day and the start go together. */
export function meetingEdits(payload: Microsoft365MeetingPayload, fields: MeetingFields): Microsoft365MeetingEdits | undefined {
  const before = meetingFields(payload);
  const edits: Microsoft365MeetingEdits = { kind: 'meeting' };
  if (fields.subject !== before.subject) edits.subject = fields.subject;
  if (fields.date !== before.date || fields.start !== before.start) {
    edits.date = fields.date;
    edits.start = fields.start;
  }
  if (fields.durationMinutes !== before.durationMinutes) edits.durationMinutes = fields.durationMinutes;
  if (!samePeople(payload.attendees, fields.attendees)) edits.attendees = fields.attendees;
  if (normalizeLineEnds(fields.note) !== payload.note) edits.note = normalizeLineEnds(fields.note);
  return Object.keys(edits).length > 1 ? edits : undefined;
}

/** The meeting as the card shows it while edited; null when the day or the time cannot be read yet. */
export function editedMeeting(payload: Microsoft365MeetingPayload, fields: MeetingFields): Microsoft365MeetingPayload | null {
  const start = instantAt(fields.date, fields.start, payload.timeZone);
  if (!start || !Number.isFinite(fields.durationMinutes)) return null;
  const end = new Date(start.getTime() + fields.durationMinutes * 60_000);
  return { ...payload, subject: fields.subject, start: start.toISOString(), end: end.toISOString(), attendees: fields.attendees, note: normalizeLineEnds(fields.note) };
}

/** A typed address as a recipient (no name: the person's own words), or null when it is not one. */
export function typedRecipient(text: string): Microsoft365Recipient | null {
  const address = text.trim().replace(/^mailto:/i, '');
  return microsoft365Address.safeParse(address).success ? { name: '', address } : null;
}

export type EditProblem = 'to' | 'tooMany' | 'duplicate' | 'subject' | 'subjectLine' | 'body' | 'title' | 'date' | 'start' | 'duration' | 'attendees' | 'note';

// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f]/;
const unique = (people: readonly { address: string }[]) => new Set(people.map((p) => lower(p.address))).size === people.length;

/** What keeps an edited mail from going (the same limits the server checks). */
export function mailProblems(fields: MailFields): EditProblem[] {
  const out: EditProblem[] = [];
  const all = [...fields.to, ...fields.cc];
  if (fields.to.length === 0) out.push('to');
  if (all.length > MICROSOFT365_WRITE_LIMITS.recipients) out.push('tooMany');
  if (!unique(all)) out.push('duplicate');
  if (fields.subject.length > MICROSOFT365_WRITE_LIMITS.subject) out.push('subject');
  if (CONTROL.test(fields.subject)) out.push('subjectLine');
  if (normalizeLineEnds(fields.body).length > MICROSOFT365_WRITE_LIMITS.body) out.push('body');
  return out;
}

/** What keeps an edited meeting from being created. */
export function meetingProblems(fields: MeetingFields, timeZone: string): EditProblem[] {
  const out: EditProblem[] = [];
  if (!fields.subject.trim() || fields.subject.length > MICROSOFT365_WRITE_LIMITS.subject || CONTROL.test(fields.subject)) out.push('title');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fields.date) || !instantAt(fields.date, '12:00', timeZone)) out.push('date');
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(fields.start)) out.push('start');
  if (!Number.isInteger(fields.durationMinutes) || fields.durationMinutes < MICROSOFT365_WRITE_LIMITS.minMinutes || fields.durationMinutes > MICROSOFT365_WRITE_LIMITS.maxMinutes) out.push('duration');
  if (fields.attendees.length > MICROSOFT365_WRITE_LIMITS.attendees) out.push('attendees');
  if (!unique(fields.attendees)) out.push('duplicate');
  if (normalizeLineEnds(fields.note).length > MICROSOFT365_WRITE_LIMITS.note) out.push('note');
  return out;
}

// ---- states in words ----------------------------------------------------------------------------------

export type HeaderTone = 'waiting' | 'done' | 'failed' | 'unknown' | 'quiet';

export interface WriteHeader {
  tone: HeaderTone;
  title: string;
  /** a second line: the reason of a failure, the Teams line of a meeting */
  detail: string | null;
  /** v0.16c.6 (D-416): the card offers "Yeni mail olarak hazırla" (a reply Microsoft could not open) */
  action?: 'asNewMail';
}

/**
 * A finished card's header: "Gönderildi · 11:52", "Oluşturuldu · davetler gönderildi" with its Teams line,
 * "Gönderilemedi" with why, the unknown end ("… Outlook'ta Gönderilmiş Öğeler'e bakın.", no retry), expired,
 * cancelled ("vazgeçtiniz") and stopped ("durduruldu"). Pending and sending are the card's own (null here).
 */
export function outcomeHeader(
  state: WriteState,
  kind: Microsoft365WriteKind,
  outcome: Pick<Microsoft365WriteOutcome, 'code' | 'online' | 'at'> | null,
  ctx: { t: Translate; timeZone: string; attendees: number },
): WriteHeader | null {
  const { t } = ctx;
  switch (state) {
    case 'pending':
      return null;
    case 'sent': {
      const time = outcome ? clockOf(outcome.at, cardZone(ctx.timeZone)) : '';
      return { tone: 'done', title: time ? t(`${K}.state.sent`, { time }) : t(`${K}.state.sentNoTime`), detail: null };
    }
    case 'created':
      return {
        tone: 'done',
        title: t(ctx.attendees > 0 ? `${K}.state.created` : `${K}.state.createdAlone`),
        detail: outcome?.online === false ? t(`${K}.state.noTeams`) : outcome?.online === true ? t(ctx.attendees > 0 ? `${K}.state.teams` : `${K}.state.teamsAlone`) : null,
      };
    case 'failed':
      // v0.16c.6 (D-415): Exchange could not read the answered mail; nothing was made, it can go as a new mail
      if (kind === 'mail' && outcome?.code === 'original_unreadable') return { tone: 'failed', title: t(`${K}.state.unreadable.title`), detail: t(`${K}.state.unreadable.detail`), action: 'asNewMail' };
      return { tone: 'failed', title: t(`${K}.state.failed.${kind}`), detail: [failureReason(outcome?.code, kind, t), t(`${K}.state.askAgain`)].join(' ') };
    case 'unknown':
      return { tone: 'unknown', title: t(`${K}.state.unknown.${kind}`), detail: null };
    default:
      return { tone: 'quiet', title: t(`${K}.state.${state}.${kind}`), detail: null };
  }
}

const REASONS = new Set(['original_unreadable', 'draft_changed', 'verify_mismatch', 'microsoft_busy', 'microsoft_forbidden', 'microsoft_not_found', 'microsoft_rejected', 'microsoft_failed', 'teams_unavailable', 'timeout', 'microsoft_unavailable']);

/** Why a write failed, in plain words (never Microsoft's text, never the raw code). */
export function failureReason(code: string | undefined, kind: Microsoft365WriteKind, t: Translate): string {
  if (code === 'microsoft_not_found') return t(`${K}.reasons.microsoft_not_found.${kind}`);
  return REASONS.has(code ?? '') ? t(`${K}.reasons.${code}`) : t(`${K}.reasons.other`);
}

/** The words of a pending card's header: "Onayınızı bekliyor · Mail" and its lock, "Oppy onaysız mail göndermez". */
export function pendingHeader(kind: Microsoft365WriteKind, mode: Microsoft365MailPayload['mode'] | null, t: Translate): { title: string; kind: string; lock: string } {
  return {
    title: t(`${K}.waiting`),
    kind: t(`${K}.kind.${mode === 'draft' ? 'draft' : mode === 'reply_as_new' ? 'asNew' : kind}`),
    lock: t(`${K}.lock.${kind}`),
  };
}

/**
 * v0.16c.6 (D-413, D-414): the notes under a reply sent as a new mail: the answered mail's attachments do not go, and
 * without the answered mail's Message-ID Outlook may show it as a conversation of its own. Null for other mails.
 */
export function asNewNotes(payload: Pick<Microsoft365MailPayload, 'mode' | 'thread'>, t: Translate): string[] | null {
  if (payload.mode !== 'reply_as_new') return null;
  return [t(`${K}.asNew.attachments`), ...(payload.thread ? [] : [t(`${K}.asNew.separate`)])];
}

// ---- the views by kind -----------------------------------------------------------------------------------

export const mailViewOf = (view: Microsoft365WriteView | null): Microsoft365MailView | null => (view?.kind === 'mail' ? view : null);
export const meetingViewOf = (view: Microsoft365WriteView | null): Microsoft365MeetingView | null => (view?.kind === 'meeting' ? view : null);
